import { Event, track } from 'app/lib/analytics';
import React from 'react';

export const RESET = '\x1b[0m';
export const CYAN = '\x1b[36m';
export const YELLOW = '\x1b[33m';
export const MAGENTA = '\x1b[35m';

const MAX_PENDING_IDS_IN_LOG = 5;

const log = (screenName: string, message: string) => {
  if (!__DEV__) return;

  console.log(`${CYAN}[ScreenPerf]${RESET}${MAGENTA}[${screenName}]${RESET} ${YELLOW}${message}${RESET}`);
};

function formatPendingIds(pendingIds: string[]) {
  if (pendingIds.length === 0) return '';

  const visibleIds = pendingIds.slice(0, MAX_PENDING_IDS_IN_LOG);
  const hiddenCount = pendingIds.length - visibleIds.length;
  const hiddenSummary = hiddenCount > 0 ? `, +${hiddenCount.toFixed(0)} more` : '';
  return ` [${visibleIds.join(', ')}${hiddenSummary}]`;
}

interface LoadingTrackers {
  id: string;
  getIsLoading: () => boolean;
}

export function useScreenPerfMonitor(screenName: string) {
  const trackersRef = React.useRef<Map<string, LoadingTrackers>>(new Map());
  const startTimeRef = React.useRef<number | null>(null);
  const hasStartedLoading = React.useRef(false);
  const hasSentEvent = React.useRef(false);
  const pendingCheckRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastTrackerStateRef = React.useRef<string | null>(null);
  const hasLoggedCachedLoadRef = React.useRef(false);

  const checkAndSendEvent = () => {
    if (hasSentEvent.current) return;

    let trackerCount = 0;
    const pendingIds: string[] = [];
    for (const tracker of trackersRef.current.values()) {
      trackerCount += 1;
      if (tracker.getIsLoading()) pendingIds.push(tracker.id);
    }
    if (trackerCount === 0) return;

    const trackerState = `${trackerCount.toFixed(0)}:${pendingIds.join('\0')}`;
    if (trackerState !== lastTrackerStateRef.current) {
      lastTrackerStateRef.current = trackerState;
      log(
        screenName,
        `Tracker transition: ${pendingIds.length.toFixed(0)}/${trackerCount.toFixed(0)} pending${formatPendingIds(pendingIds)}`,
      );
    }

    const anyLoading = pendingIds.length > 0;

    // Start timer on first loading state
    if (anyLoading && !hasStartedLoading.current) {
      hasStartedLoading.current = true;
      startTimeRef.current = performance.now();
      log(screenName, '⏱️  Loading started');
      return;
    }

    // Only send event if loading has started AND all finished
    const allFinished = pendingIds.length === 0;

    if (allFinished && hasStartedLoading.current && startTimeRef.current !== null) {
      const timeTaken = performance.now() - startTimeRef.current;

      hasSentEvent.current = true;
      log(screenName, `✅ Initial load complete in ${timeTaken.toFixed(2)}ms`);
      track(Event.Metrics, {
        screen_name: screenName,
        load_time: timeTaken.toFixed(2),
      });
    } else if (allFinished && !hasStartedLoading.current && !hasLoggedCachedLoadRef.current) {
      hasLoggedCachedLoadRef.current = true;
      log(screenName, '⚠️ All trackers finished but loading never started (cached data)');
    }
  };

  const scheduleCheck = () => {
    // Cancel any pending check to avoid multiple checks
    if (pendingCheckRef.current) {
      clearTimeout(pendingCheckRef.current);
    }

    pendingCheckRef.current = setTimeout(() => {
      checkAndSendEvent();
      pendingCheckRef.current = null;
    }, 0);
  };

  const registerLoading = (id: string, getIsLoading: () => boolean) => {
    const isCurrentlyLoading = getIsLoading();
    log(screenName, `Registering tracker "${id}" with isLoading=${String(isCurrentlyLoading)}`);
    trackersRef.current.set(id, { id, getIsLoading });

    scheduleCheck();

    return () => {
      trackersRef.current.delete(id);
    };
  };

  const notifyLoadingChange = () => {
    scheduleCheck();
  };

  // Cleanup pending timeout on unmount
  React.useEffect(() => {
    return () => {
      if (pendingCheckRef.current) {
        clearTimeout(pendingCheckRef.current);
      }
    };
  }, []);

  const contextValue = { registerLoading, notifyLoadingChange };

  return { contextValue };
}
